// ── Shared pre-match cinematic for the arena games: the emblem is struck,
//    the table is named, the contenders are called, the round is opened. ──

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * theme — { accent, accentSoft, text, ring } colours
 * emblem — glyph struck in the opening beat
 * title / tagline / rule — the naming beat
 * cry — the final call to play
 */
const GameIntro = ({
  emblem = '✦', title = '', tagline = '', rule = '', cry = 'VIA',
  players = [], theme, onDone,
}) => {
  const [phase, setPhase] = useState(0);
  const t = theme || { accent: '#60a5fa', accentSoft: 'rgba(96,165,250,0.5)', text: '#dbeafe', ring: 'rgba(96,165,250,0.55)' };

  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 1350),
      setTimeout(() => setPhase(2), 3700),
      setTimeout(() => onDone?.(), 5000),
    ];
    return () => timers.forEach(clearTimeout);
  }, [onDone]);

  return (
    <motion.div
      data-testid="game-intro"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.6 }}
      onClick={() => onDone?.()}
      style={{
        position: 'fixed', inset: 0, zIndex: 300, cursor: 'pointer', overflow: 'hidden',
        background: 'radial-gradient(ellipse at 50% 55%, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.85) 62%, rgba(0,0,0,0.96) 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {/* ── Beat 1: the emblem is struck ── */}
      <AnimatePresence>
        {phase === 0 && (
          <motion.div
            key="emblem"
            initial={{ opacity: 0, scale: 2.6, rotateY: -120 }}
            animate={{ opacity: 1, scale: 1, rotateY: 0 }}
            exit={{ opacity: 0, scale: 0.4 }}
            transition={{ type: 'spring', stiffness: 130, damping: 15 }}
            style={{ position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <motion.div
              animate={{ scale: [1, 2.6], opacity: [0.7, 0] }}
              transition={{ duration: 1.3, repeat: Infinity, ease: 'easeOut' }}
              style={{ position: 'absolute', width: 190, height: 190, borderRadius: '50%', border: `2px solid ${t.ring}` }}
            />
            <div style={{ fontSize: 'clamp(58px,12vw,120px)', lineHeight: 1, filter: `drop-shadow(0 0 40px ${t.accent})` }}>
              {emblem}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Beat 2: the table is named ── */}
      <AnimatePresence>
        {phase === 1 && (
          <motion.div
            key="title"
            initial={{ opacity: 0, scale: 1.18, filter: 'blur(8px)' }}
            animate={{ opacity: 1, scale: 1, filter: 'blur(0px)' }}
            exit={{ opacity: 0, scale: 0.94 }}
            transition={{ duration: 0.65, ease: 'easeOut' }}
            style={{ textAlign: 'center', padding: '0 22px', position: 'relative' }}
          >
            <motion.div
              animate={{ scale: [1, 1.08, 1], filter: [`drop-shadow(0 0 16px ${t.accent})`, `drop-shadow(0 0 38px ${t.accent})`, `drop-shadow(0 0 16px ${t.accent})`] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
              style={{ fontSize: 'clamp(40px,8vw,74px)', lineHeight: 1, marginBottom: 6 }}
            >
              {emblem}
            </motion.div>
            <div style={{ fontSize: 10, letterSpacing: '0.42em', color: t.accentSoft, fontWeight: 800, marginBottom: 10 }}>
              SURVIVAL GAMES
            </div>
            <h1 style={{
              fontFamily: "'Cinzel',serif", margin: 0,
              fontSize: 'clamp(26px,6.5vw,58px)', fontWeight: 900, letterSpacing: '0.04em',
              background: `linear-gradient(to bottom, #ffffff 0%, ${t.text} 45%, ${t.accent} 100%)`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              filter: 'drop-shadow(0 6px 18px rgba(0,0,0,0.9))',
            }}>
              {title}
            </h1>
            {tagline && (
              <div style={{ fontSize: 13, fontStyle: 'italic', color: 'rgba(255,255,255,0.45)', marginTop: 8, letterSpacing: '0.1em' }}>
                « {tagline} »
              </div>
            )}
            {rule && (
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}
                style={{
                  marginTop: 16, display: 'inline-block', padding: '6px 16px', borderRadius: 9,
                  border: `1px solid ${t.accent}44`, background: 'rgba(0,0,0,0.5)',
                  fontSize: 12, fontWeight: 700, color: t.text, letterSpacing: '0.05em',
                }}
              >
                {rule}
              </motion.div>
            )}
            <motion.div
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
              style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginTop: 20 }}
            >
              {players.slice(0, 4).map((p, i) => (
                <motion.div key={i}
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.7 + i * 0.12 }}
                  style={{
                    padding: '5px 13px', borderRadius: 8,
                    border: `1px solid ${t.accent}55`, background: 'rgba(0,0,0,0.55)',
                    fontSize: 11, fontWeight: 800, color: t.accent,
                    fontFamily: "'Cinzel',serif", letterSpacing: '0.06em',
                  }}
                >
                  {p}
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Beat 3: the call ── */}
      <AnimatePresence>
        {phase === 2 && (
          <motion.div
            key="cry"
            initial={{ opacity: 0, scale: 1.7 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 1.1 }}
            transition={{ type: 'spring', stiffness: 150, damping: 14 }}
            style={{ position: 'relative', textAlign: 'center' }}
          >
            <motion.div
              animate={{ scale: [1, 4], opacity: [0.7, 0] }}
              transition={{ duration: 1, ease: 'easeOut' }}
              style={{ position: 'absolute', top: '50%', left: '50%', width: 220, height: 220, marginTop: -110, marginLeft: -110, borderRadius: '50%', border: `2px solid ${t.accent}` }}
            />
            <h1 style={{
              fontFamily: "'Cinzel',serif", margin: 0, whiteSpace: 'nowrap',
              fontSize: 'clamp(30px,8vw,78px)', fontWeight: 900, letterSpacing: '0.1em',
              background: `linear-gradient(to bottom, #ffffff, ${t.accent} 55%, rgba(0,0,0,0.6) 130%)`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              filter: `drop-shadow(0 0 30px ${t.accentSoft})`,
            }}>
              {cry}
            </h1>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={(e) => { e.stopPropagation(); onDone?.(); }}
        style={{
          position: 'absolute', bottom: 22, right: 22, zIndex: 5,
          background: 'rgba(0,0,0,0.6)', border: `1px solid ${t.accent}44`,
          borderRadius: 9, padding: '6px 14px', color: t.accentSoft,
          fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', cursor: 'pointer',
        }}
      >
        SALTA ›
      </button>
    </motion.div>
  );
};

export default GameIntro;
